// Loyalty expiry helpers.
// Core sends next_expiry_on / next_expiry_amount in loyalty_details only for
// companies with loyalty expiry turned on; otherwise the keys are absent.

// Only warn about loyalty expiring within this many days.
export const LOYALTY_EXPIRY_WINDOW_DAYS = 30;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface ILoyaltyExpiryNotice {
  amount: number;
  date: Date;
  days: number;
}

/**
 * Parse a 'YYYY-MM-DD' string as a local calendar date (midnight, local time).
 * new Date('YYYY-MM-DD') would parse it as UTC midnight, which shows as the
 * previous day in time zones behind UTC, so build it from the parts instead.
 */
export const parseCalendarDate = (value: any): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = new Date(year, month, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
};

/**
 * Whole calendar days from today to the given date (0 = today, 1 = tomorrow).
 * Rounded so a DST shift between the two midnights doesn't change the count.
 */
export const calendarDaysUntil = (date: Date, today: Date = new Date()): number => {
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((date.getTime() - startOfToday.getTime()) / MS_PER_DAY);
};

/**
 * The next loyalty expiry to show the customer, or null when there is nothing
 * to show: expiry off for the company (keys absent), nothing expiring, or the
 * next expiry is more than LOYALTY_EXPIRY_WINDOW_DAYS away.
 */
export const loyaltyExpiryNotice = (
  loyalty: any,
  today: Date = new Date()
): ILoyaltyExpiryNotice | null => {
  if (!loyalty) return null;

  const date = parseCalendarDate(loyalty.next_expiry_on);
  const amount = parseFloat(loyalty.next_expiry_amount);
  if (!date || !(amount > 0)) return null;

  const days = calendarDaysUntil(date, today);
  if (days > LOYALTY_EXPIRY_WINDOW_DAYS) return null;

  return { amount, date, days };
};
